type DataRow = (string | number | null | undefined)[];
type DataTable = Record<string, DataRow>;

const MISSING_DENOMINATOR_VALUES = new Set(["", "null", "n/a", "na", "-"]);

/**
 * Scans a data table for rows where the denominator field is missing and blanks
 * all normalized (non-denominator, non-entity-name) columns in those rows.
 *
 * @param data - Record of UWI → row array as returned by the data API
 * @param selectedProperties - Ordered array of property names matching row indices
 * @param denominatorFieldName - The field being used as the normalization denominator
 * @returns transformedData (new object, original is not mutated) and droppedCount
 */
export function applyNormalizationDrop(
  data: DataTable,
  selectedProperties: string[],
  denominatorFieldName: string
): { transformedData: DataTable; droppedCount: number } {
  const denominatorIndex = selectedProperties.indexOf(denominatorFieldName);

  if (denominatorIndex < 0) {
    return { transformedData: data, droppedCount: 0 };
  }

  // Indices of columns that should be blanked when denominator is missing:
  // everything except index 0 (entity name) and the denominator column itself.
  const normalizedColumnIndices = selectedProperties
    .map((_, idx) => idx)
    .filter((idx) => idx > 0 && idx !== denominatorIndex);

  if (normalizedColumnIndices.length === 0) {
    return { transformedData: data, droppedCount: 0 };
  }

  const transformedData: DataTable = { ...data };
  let droppedCount = 0;

  for (const uwi of Object.keys(transformedData)) {
    const row = transformedData[uwi];
    if (!Array.isArray(row) || denominatorIndex >= row.length) continue;

    const rawValue = row[denominatorIndex];
    const denominatorValue = String(rawValue ?? "")
      .trim()
      .toLowerCase();

    if (!MISSING_DENOMINATOR_VALUES.has(denominatorValue)) continue;

    // Copy the row before mutating (API rows may be read-only)
    const newRow = [...row];
    for (const colIdx of normalizedColumnIndices) {
      if (colIdx < newRow.length) {
        newRow[colIdx] = "";
      }
    }
    transformedData[uwi] = newRow;
    droppedCount++;
  }

  return { transformedData, droppedCount };
}
